import type {
    Scope,
    Category,
    Command,
    CommandAction,
    CommandActions,
    CommandInfoBase,
    CreateCommands,
    CommandEvents,
} from "./types";

import hotkeys from "hotkeys-js";
import { createSubject } from "light-observable/observable";

import { get, type Readable } from "svelte/store";
import { getEntries, type ValueWritable, valueWritable } from "..";
import { createScope } from "./scope";
import { onMount } from "svelte";

export { default as CommandButton } from "./CommandButton.svelte";
export { default as Shortcut } from "./Shortcut.svelte";

export function args<T extends unknown[]>() {
    return { args: undefined as any as T };
}


export function createCommandHandler<
    T extends {},
    ScopeId extends string,
    CategoryId extends string,
    CommandInfo extends CommandInfoBase,
>(
    scopes: Record<ScopeId, Scope>,
    categories: Record<CategoryId, Category>,
    commands: CreateCommands<CommandInfo, T>,
    addition: T = {} as T,
) {
    type CommandId = keyof CommandInfo;

    const [eventStream, eventSink] =
        createSubject<CommandEvents<CommandId, ScopeId, CommandInfo>>();

    /// Create maps
    const commandMap = createCommandMap();
    const actionMap = {} as Record<CommandId, CommandActions<any>[]>;

    const { $scope, scopeStack, actions: scopeActions, isScope, setScope } = createScope<ScopeId | "all">();

    /// Create commands

    function createCommandMap() {
        // todo move outside
        const hotkeys = loadCommandHotkeys();

        return getEntries(commands).reduce(
            (acc, [id, config]) => {
                acc[id as CommandId] = {
                    ...(config as unknown as T),

                    id: id,
                    name: config.name,
                    group: config.group,
                    category: id.split(".")[0],
                    default: config.default ?? "",
                    canRebind: config.canRebind ?? true,
                    internal: config.internal ?? false,
                    useKeyboard: config.useKeyboard ?? true,

                    keydown: config.keydown ?? true,
                    keyup: config.keyup ?? false,
                    repeat: config.repeat ?? false,

                    hotkey: hotkeys[id] ?? config.default ?? "",
                    invoke: (...args) =>
                        invokeAction(id as any, args as unknown as CommandInfo[any], true),

                    enabled: valueWritable(true),
                };

                return acc;
            },
            {} as Record<CommandId, Command & T>,
        );
    }

    function saveCommandHotkeys() {
        const hotkeys: Record<string, string> = {};

        getEntries(commandMap).forEach(([id, cmd]) => {
            if (cmd.hotkey && cmd.hotkey !== cmd.default) {
                hotkeys[id] = cmd.hotkey;
            }
        });

        eventSink.next({ type: "save-hotkeys" });
        localStorage.setItem("hotkeys", JSON.stringify(hotkeys));
    }

    function loadCommandHotkeys() {
        eventSink.next({ type: "load-hotkeys" });
        return JSON.parse(localStorage.getItem("hotkeys") ?? "{}") ?? {};
    }

    /////////////////////////////
    ///      ACTIONS          ///
    function registerAction<T extends CommandId>(
        id: T,
        action: CommandAction<CommandInfo[T]>,
        scope: ScopeId | "all",
    ) {
        const sc = { scope, invoke: action };
        actionMap[id] ??= [];
        actionMap[id].push(sc);

        eventSink.next({ type: "register-action", data: { id, action: action as any, scope } });

        return () => {
            actionMap[id].remove(sc);
        };
    }

    function registerActions(scope: ScopeId | "all") {
        const destroy: (() => void)[] = [];

        return {
            add<T extends CommandId>(command: T, action: CommandAction<CommandInfo[T]>, enabled?: Readable<boolean> | boolean) {
                if (enabled !== undefined) {
                    if (enabled === true || enabled === false) {
                        commandMap[command].enabled.set(enabled)
                    } else {
                        enabled.subscribe(v => commandMap[command].enabled.set(v));
                    }
                }
                destroy.push(registerAction(command, action, scope));
            },
            destroy() {
                destroy.forEach((d) => d());
            },
        };
    }

    function registerActionNode<T extends CommandId>(node: Node, options: {
        id: T,
        action: CommandAction<CommandInfo[T]>,
        scope: ScopeId | "all"
    }) {
        return {
            destroy: registerAction(options.id, options.action, options.scope),
        };
    }

    function invoke<T extends CommandId>(id: T, ...args: CommandInfo[T]) {
        return invokeAction(id, args);
    }

    function invokeInScope<T extends CommandId>(scope: ScopeId | "all", id: T, ...args: CommandInfo[T]) {
        const pushScope = scope && !isScope(scope);
        if (pushScope) scopeStack.push(scope);

        const result = invokeAction(id, args);

        if (pushScope) scopeStack.pop(scope);

        return result;
    }

    function invokeAction(this: any, id: CommandId, args: unknown[], external = false) {
        const enabled = commandMap[id].enabled;
        if (enabled && !get(commandMap[id].enabled)) return false;
        if (this?.repeat && !commandMap[id].repeat) return false;


        let actions: CommandActions<any>[];
        const scope = scopeStack.top();

        if (this?.type?.contains("up") || external) {
            actions = actionMap[id] ?? [];
        } else {
            actions = actionMap[id]?.filter((action) => action.scope === scope) ?? [];

            if (!(scopes[scope as ScopeId]?.blocking ?? false)) {
                const scopeAllActions = actionMap[id]?.filter((action) => action.scope === "all") ?? [];
                actions = actions.concat(scopeAllActions);
            }
        }

        if (actions.length === 0) {
            return false;
        }

        eventSink.next({ type: "invoke-action", data: { id, args: args as any, scope } });

        const start = performance.now();
        try {
            for (const action of actions) {
                const result = action.invoke.apply(this, args);
                if (result === undefined || result) return true;
            }
        } catch (e) {
            eventSink.next({ type: "error", data: { message: "Error invoking {0}", args: [id] } });
            console.error(e);
            return true;
        } finally {
            const end = performance.now();
            eventSink.next({ type: "invoked-action", data: { id, args: args as any, scope, time: end - start } });
        }

        return false;
    }

    /////////////////////////////
    ///      HOTKEYS          ///
    let triggeredHotkeyEvent: Event;
    let triggered = {} as Record<string, string>;

    function triggerHotkey(hotkey: string, e: WheelEvent | MouseEvent | KeyboardEvent, scope?: ScopeId | "all", release = false) {
        hotkeys.getPressedKeyCodes();
        triggeredHotkeyEvent = e;


        let modifiers = [];
        e.ctrlKey && modifiers.push("ctrl");
        e.altKey && modifiers.push("alt");
        e.shiftKey && modifiers.push("shift");
        e.metaKey && modifiers.push("meta");

        const cmd = [...modifiers, hotkey].join("+");

        const pushScope = scope && !isScope(scope);
        if (pushScope) scopeStack.push(scope);

        if (triggered[hotkey]) {
            if (triggered[hotkey] === cmd) {
                delete triggered[hotkey];
                hotkeys.trigger(cmd);
            } else {
                hotkeys.trigger(triggered[hotkey]);
                triggered[hotkey] = cmd;
                hotkeys.trigger(cmd);
            }
        } else {
            if (release) triggered[hotkey] = cmd;
            hotkeys.trigger(cmd);
        }

        if (pushScope) scopeStack.pop(scope);
    }

    function registerKeybindings() {
        hotkeys.unbind();

        eventSink.next({ type: "register-hotkeys" });

        function cmdAction(id: CommandId) {
            return (e: Event) => {
                e ??= triggeredHotkeyEvent;
                if (invokeAction.apply(e, [id, []])) {
                    e?.preventDefault();
                    e?.stopPropagation();
                }
            };
        }

        Object.values<Command>(commandMap).forEach((cmd) => {
            if (!cmd.useKeyboard) return;
            hotkeys(
                cmd.hotkey,
                { scope: "all", keydown: cmd.keydown, keyup: cmd.keyup },
                cmdAction(cmd.id as CommandId),
            );
        });
    }

    function rebindHotkey(id: CommandId, keybinding: string) {
        const command = commandMap[id];
        if (!command.canRebind) return;

        eventSink.next({ type: "rebind-hotkey", data: { id, keybinding } });
        command.hotkey = keybinding;
    }

    hotkeys.filter = () => {
        return true;
    };

    /////////////////////////////
    ///    MOUSE COMMANDS     ///
    function registerMouseEvents(element: HTMLElement | Window, scope?: ScopeId | "all") {
        function onWheel(e: WheelEvent) {
            if (e.deltaY > 0) {
                triggerHotkey("mouse-scroll-down", e, scope);
            } else if (e.deltaY < 0) {
                triggerHotkey("mouse-scroll-up", e, scope);
            } else if (e.deltaX > 0) {
                triggerHotkey("mouse-scroll-right", e, scope);
            } else if (e.deltaX < 0) {
                triggerHotkey("mouse-scroll-left", e, scope);
            }
        }

        function onMouseDown(e: MouseEvent) {
            triggerHotkey(translateToMouseKey(e.button)!, e, scope, true);
        }

        function onMouseUp(e: MouseEvent) {
            triggerHotkey(translateToMouseKey(e.button)!, e, scope);
        }

        (element as HTMLElement).addEventListener("wheel", onWheel);
        (element as HTMLElement).addEventListener("mousedown", onMouseDown);
        (element as HTMLElement).addEventListener("mouseup", onMouseUp);

        function unregisterWindowEvents() {
            (element as HTMLElement).removeEventListener("wheel", onWheel);
            (element as HTMLElement).removeEventListener("mousedown", onMouseDown);
            (element as HTMLElement).removeEventListener("mouseup", onMouseUp);
        }

        return {
            destroy: unregisterWindowEvents,
        };
    }

    function isMouseCommandPressed(command: Command, e: MouseEvent) {
        const commands = command.hotkey.split(",").map((c) => c.trim());

        const result = commands.find((keyCombo) => {
            if (isMouseCommand(keyCombo)) {
                return keyCombo.split("+").every((key) => {
                    if (isMouseCommand(key)) {
                        const mouseKey = translateFromMouseKey(key);
                        return !!(e.buttons & (mouseKey as number));
                    } else if (key === "shift" && e.shiftKey) {
                        return true;
                    } else if (key === "ctrl" && e.ctrlKey) {
                        return true;
                    } else if (key === "alt" && e.altKey) {
                        return true;
                    } else if (key === "meta" && e.metaKey) {
                        return true;
                    } else {
                        return hotkeys.isPressed(key);
                    }
                });
            } else {
                return hotkeys.isPressed(keyCombo);
            }
        });
        return !!result;
    }

    function isMouseCommand(key: string) {
        return key.indexOf("mouse") !== -1;
    }

    function translateFromMouseKey(key: string) {
        switch (key) {
            case "mouse-left":
                return 1;
            case "mouse-right":
                return 2;
            case "mouse-middle":
                return 4;
        }
    }

    function translateToMouseKey(key: number) {
        switch (key) {
            case 0:
                return "mouse-left";
            case 1:
                return "mouse-middle";
            case 2:
                return "mouse-right";
            case 3:
                return "mouse-back";
            case 4:
                return "mouse-forward";
        }
    }

    registerKeybindings();

    function registerComponentActions(scope: ScopeId | "all") {
        const toAdd = [] as [CommandId, CommandAction<any>, ScopeId | "all"][];

        onMount(() => {
            const destroy: (() => void)[] = [];

            for (const info of toAdd) {
                destroy.push(registerAction(info[0], info[1], info[2]));
            }

            return () => destroy.forEach((d) => d());
        });

        const int =  {
            add<T extends CommandId>(command: T, action: CommandAction<CommandInfo[T]>, scp = scope) {
                toAdd.push([command, action, scp]);
                return int;
            }
        }

        return int;
    }

    /////////////////////////////
    ///      RETURN           ///
    return {
        invoke,
        invokeInScope,
        registerAction,
        registerActions,
        registerComponentActions,

        registerMouseEvents,

        registerKeybindings,
        saveCommandHotkeys,
        rebindHotkey,

        isScope,
        isMouseCommandPressed,

        setScope,

        scopes: {
            ...scopes,
            all: { name: "All" },
        } as Record<ScopeId | "all", Scope>,
        categories,
        commands: commandMap,

        scope: $scope as ValueWritable<ScopeId | "all">,

        actions: {
            ...scopeActions,
            register: registerActionNode,
        },

        events: eventStream,
    };
}

export type CommandHandler<Id = any, Scopes = "all"> = {
    rebindHotkey: (id: Id, keybinding: string) => void;
    registerKeybindings: () => void;
    registerMouseEvents: (element: HTMLElement | Window, scope?: Scopes) => { destroy: () => void };

    registerActions: (scope: Scopes) => {
        add: (command: Id, action: CommandAction<any>) => void,
        destroy: () => void
    }
    registerAction: (id: Id, action: CommandAction<any>, scope: any) => () => void;

    setScope: (scope: any) => () => void;

    scopes: Record<string, Scope>;
    categories: Record<string, Category>;
    commands: Record<string, Command<any>>;

    scope: ValueWritable<string>;

    actions: {
        nodeScope: (node: Node, scope: (Scopes)) => { destroy(): void },
        mouseScope: (node: Node, scope: (Scopes)) => { destroy(): void },
        keyboardScope: (node: Node, scope: (Scopes)) => {}
    }
};


export type { Command } from "./types";